import { Link } from "react-router-dom";
import { ArrowRight, Activity, ShieldCheck, Database, Eye, Zap, Radio } from "lucide-react";
import TimelineBar from "../components/TimelineBar";

const FEATURES = [
    {
        Icon: Zap,
        title: "8-Second Protocol",
        body: "Every clip is engineered to exactly eight seconds. Hook, misdirection, snap. No outro. No mercy.",
    },
    {
        Icon: ShieldCheck,
        title: "Governed Safety Matrix",
        body: "Six risk dimensions scored per clip. Platform risk or harm risk ≥ 7 triggers auto-rewrite.",
    },
    {
        Icon: Database,
        title: "Real Data Humor",
        body: "Themes sourced from economic, cultural, and institutional signal. No fake statistics. Ever.",
    },
    {
        Icon: Eye,
        title: "Psychological Signal",
        body: "Aggregate reaction tracking — scroll-stop, replay, \"too real\" frequency. No individual profiling.",
    },
    {
        Icon: Activity,
        title: "Platform-Native Export",
        body: "TikTok, Reels, Shorts. Captions, hashtags, voiceover and visual direction bundled.",
    },
    {
        Icon: Radio,
        title: "Modular AI Core",
        body: "Claude / GPT / Gemini — swappable via environment. Prompts are editable templates.",
    },
];

export const Landing = () => {
    return (
        <div data-testid="landing-page" className="min-h-screen">
            {/* HERO */}
            <section className="relative noise-bg grid-bg overflow-hidden">
                <div className="relative z-10 px-6 md:px-12 lg:px-24 pt-20 pb-28 lg:pt-28 lg:pb-40">
                    <div className="flex items-center gap-3 mb-10 reveal-up">
                        <div className="w-2 h-2 bg-[#ff2a2a] blink" />
                        <span className="d8-label">Live / Signal Stable / v1.0</span>
                    </div>

                    <h1
                        data-testid="hero-title"
                        className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-5xl sm:text-7xl lg:text-[9rem] leading-[0.88] max-w-[14ch] reveal-up"
                        style={{ animationDelay: "0.1s" }}
                    >
                        Eight<br />
                        seconds of<br />
                        <span className="text-[#ff2a2a]">engineered</span> dread.
                    </h1>

                    <p
                        className="mt-8 max-w-xl text-[#8a8a8a] text-base md:text-lg leading-relaxed reveal-up"
                        style={{ animationDelay: "0.2s" }}
                    >
                        Dark8-s is a governed AI media system for deadpan, socially-observant, platform-safe dark comedy.
                        Not a joke generator. A reaction engine — precision-timed, safety-scored, and built for scroll interruption.
                    </p>

                    <div
                        className="mt-10 flex flex-wrap items-center gap-3 reveal-up"
                        style={{ animationDelay: "0.3s" }}
                    >
                        <Link
                            to="/generator"
                            data-testid="hero-cta-generate"
                            className="inline-flex items-center gap-3 bg-[#ff2a2a] hover:bg-[#cc1a1a] text-[#f5f5f0] font-heading font-bold uppercase tracking-wide px-6 py-4 transition-colors"
                        >
                            Generate a clip
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/library"
                            data-testid="hero-cta-library"
                            className="inline-flex items-center gap-3 border border-[#262626] hover:bg-[#121212] text-[#f5f5f0] font-heading font-bold uppercase tracking-wide px-6 py-4 transition-colors"
                        >
                            Browse seed library
                        </Link>
                    </div>

                    {/* Timeline hero */}
                    <div
                        className="mt-20 lg:mt-28 max-w-3xl reveal-up"
                        style={{ animationDelay: "0.4s" }}
                    >
                        <div className="d8-label mb-3">/ Protocol</div>
                        <TimelineBar testid="hero-timeline" />
                        <div className="mt-5 grid grid-cols-3 gap-4">
                            <div>
                                <div className="font-mono-d8 text-[10px] text-[#8a8a8a] uppercase tracking-widest mb-1">
                                    0:00 → 0:02
                                </div>
                                <div className="font-heading text-[#f5f5f0] uppercase font-bold text-sm">
                                    Hook
                                </div>
                                <div className="text-xs text-[#8a8a8a] mt-1">Pattern break.</div>
                            </div>
                            <div>
                                <div className="font-mono-d8 text-[10px] text-[#8a8a8a] uppercase tracking-widest mb-1">
                                    0:02 → 0:06
                                </div>
                                <div className="font-heading text-[#f5f5f0] uppercase font-bold text-sm">
                                    Misdirection
                                </div>
                                <div className="text-xs text-[#8a8a8a] mt-1">Expectation built.</div>
                            </div>
                            <div>
                                <div className="font-mono-d8 text-[10px] text-[#ff2a2a] uppercase tracking-widest mb-1">
                                    0:06 → 0:08
                                </div>
                                <div className="font-heading text-[#ff2a2a] uppercase font-bold text-sm">
                                    Snap Twist
                                </div>
                                <div className="text-xs text-[#8a8a8a] mt-1">Cut immediately.</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Reaction target */}
            <section className="border-t border-[#262626] bg-[#0a0a0a] noise-bg">
                <div className="px-6 md:px-12 lg:px-24 py-20 grid md:grid-cols-3 gap-px bg-[#262626]">
                    {[
                        ["01", '"Wait… what?"', "Pattern breaks on impact."],
                        ["02", '"That\'s messed up."', "The floor drops."],
                        ["03", '"…okay that\'s actually funny."', "Uncomfortable recognition."],
                    ].map(([n, q, sub]) => (
                        <div key={n} className="bg-[#050505] p-8 md:p-12">
                            <div className="font-mono-d8 text-[10px] text-[#8a8a8a] uppercase tracking-widest mb-4">
                                STAGE {n}
                            </div>
                            <div className="font-heading text-2xl md:text-3xl font-black uppercase text-[#f5f5f0] leading-tight">
                                {q}
                            </div>
                            <div className="mt-4 text-sm text-[#8a8a8a]">{sub}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Feature grid */}
            <section className="px-6 md:px-12 lg:px-24 py-24 lg:py-32">
                <div className="mb-14">
                    <div className="d8-label mb-3">/ System modules</div>
                    <h2 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-3xl md:text-5xl leading-none max-w-3xl">
                        Not a meme toy. A control surface.
                    </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#262626] border border-[#262626]">
                    {FEATURES.map((f, i) => (
                        <div
                            key={f.title}
                            className="bg-[#050505] p-8 hover:bg-[#121212] transition-colors group"
                        >
                            <div className="flex items-center gap-3 mb-5">
                                <div className="w-9 h-9 border border-[#262626] flex items-center justify-center group-hover:border-[#ff2a2a] transition-colors">
                                    <f.Icon className="w-4 h-4 text-[#f5f5f0]" strokeWidth={1.5} />
                                </div>
                                <div className="font-mono-d8 text-[10px] text-[#8a8a8a] uppercase tracking-widest">
                                    MOD.{String(i + 1).padStart(2, "0")}
                                </div>
                            </div>
                            <h3 className="font-heading font-bold uppercase text-[#f5f5f0] text-lg mb-2">
                                {f.title}
                            </h3>
                            <p className="text-sm text-[#8a8a8a] leading-relaxed">{f.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Safety / Governance */}
            <section className="border-t border-[#262626] bg-[#0a0a0a] px-6 md:px-12 lg:px-24 py-24 lg:py-32 noise-bg">
                <div className="grid lg:grid-cols-2 gap-16">
                    <div>
                        <div className="d8-label mb-3">/ Safety & Governance</div>
                        <h2 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-3xl md:text-5xl leading-none">
                            Dark, not reckless.
                        </h2>
                        <p className="mt-6 text-[#8a8a8a] leading-relaxed max-w-xl">
                            Every clip is gated by a six-dimension risk matrix. Protected-class targeting, real-tragedy
                            exploitation, self-harm mockery, extremist framing, and medical misinformation are hard-blocked.
                        </p>
                    </div>
                    <div className="grid grid-cols-2 gap-px bg-[#262626] border border-[#262626]">
                        {[
                            "Hook",
                            "Twist",
                            "Laugh discomfort",
                            "Replay potential",
                            "Platform risk",
                            "Harm risk",
                        ].map((k) => (
                            <div key={k} className="bg-[#050505] p-5">
                                <div className="font-mono-d8 text-[10px] text-[#8a8a8a] uppercase tracking-widest mb-2">
                                    {k}
                                </div>
                                <div className="font-mono-d8 text-xl text-[#f5f5f0]">0-10</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Data humor */}
            <section className="px-6 md:px-12 lg:px-24 py-24 lg:py-32">
                <div className="grid lg:grid-cols-[1fr_1.2fr] gap-16">
                    <div>
                        <div className="d8-label mb-3">/ Real Data Humor Engine</div>
                        <h2 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-3xl md:text-5xl leading-none">
                            Jokes that recognize<br />the room.
                        </h2>
                        <p className="mt-6 text-[#8a8a8a] leading-relaxed max-w-xl">
                            Themes mapped to lived cultural pressure: inflation, housing, burnout, AI anxiety, loneliness,
                            dating fatigue, healthcare bureaucracy, student debt, institutional distrust. The system never
                            fabricates statistics.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2 content-start">
                        {[
                            "inflation", "housing_affordability", "workplace_burnout", "ai_job_anxiety",
                            "loneliness", "dating_fatigue", "healthcare_wait_times", "student_debt",
                            "consumer_debt", "institutional_distrust", "existential_absurdity", "customer_service_nightmare"
                        ].map((t) => (
                            <span
                                key={t}
                                className="font-mono-d8 text-xs uppercase tracking-widest text-[#f5f5f0] border border-[#262626] px-3 py-2 hover:border-[#ff2a2a] hover:text-[#ff2a2a] transition-colors"
                            >
                                {t.replace(/_/g, " ")}
                            </span>
                        ))}
                    </div>
                </div>
            </section>

            {/* Psychological signal */}
            <section className="relative border-t border-[#262626] bg-[#0a0a0a] px-6 md:px-12 lg:px-24 py-24 lg:py-32 noise-bg scanline overflow-hidden">
                <div className="max-w-4xl">
                    <div className="d8-label mb-3">/ Psychological Signal Experiment</div>
                    <h2 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-3xl md:text-5xl leading-none">
                        Culture laughs at what it cannot say.
                    </h2>
                    <p className="mt-6 text-[#8a8a8a] leading-relaxed max-w-2xl">
                        The Signal dashboard aggregates retention, replay, share velocity, and comment clustering across
                        generated clips — strictly anonymized and population-level. No individual profiling.
                        No diagnosis. Never.
                    </p>
                    <Link
                        to="/dashboard"
                        data-testid="signal-cta"
                        className="inline-flex mt-8 items-center gap-3 border border-[#262626] hover:border-[#ff2a2a] text-[#f5f5f0] font-heading font-bold uppercase tracking-wide px-6 py-4 transition-colors"
                    >
                        Enter Signal Dashboard
                        <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            {/* CTA */}
            <section className="px-6 md:px-12 lg:px-24 py-24 lg:py-32 border-t border-[#262626]">
                <div className="max-w-4xl">
                    <div className="d8-label mb-3">/ Deploy</div>
                    <h2 className="font-heading font-black uppercase tracking-tighter text-[#f5f5f0] text-3xl md:text-6xl leading-none">
                        Generate something<br />you shouldn't laugh at.
                    </h2>
                    <div className="mt-10">
                        <Link
                            to="/generator"
                            data-testid="footer-cta"
                            className="inline-flex items-center gap-3 bg-[#ff2a2a] hover:bg-[#cc1a1a] text-[#f5f5f0] font-heading font-bold uppercase tracking-wide px-8 py-5 text-lg transition-colors"
                        >
                            Open Generator
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                    </div>
                </div>
            </section>

            <footer className="border-t border-[#262626] px-6 md:px-12 lg:px-24 py-8">
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                    <div className="font-mono-d8 text-[10px] uppercase tracking-widest text-[#8a8a8a]">
                        DARK8-S / ENTERPRISE BUILD / SAFETY MATRIX v1 / EXACT 8.00s
                    </div>
                    <div className="font-mono-d8 text-[10px] uppercase tracking-widest text-[#8a8a8a]">
                        AGGREGATE SIGNAL ONLY · NO INDIVIDUAL PROFILING
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default Landing;
